import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function formatISTDate(dateStr) {
  if (!dateStr) return '';
  // attendance_date comes back from the API as a plain "YYYY-MM-DD" string
  // (formatted server-side via DATE_FORMAT), so parsing/displaying in UTC
  // avoids the browser's local timezone shifting it to the wrong day.
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function AdminAttendanceReport() {
  const [records, setRecords] = useState([]);
  const [batches, setBatches] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [batchId, setBatchId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchBatches = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/batches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) setBatches(response.data.batches || []);
    } catch (err) {
      // Non-fatal — the filter dropdown just stays empty.
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const params = {};
      if (studentSearch) params.studentSearch = studentSearch;
      if (batchId) params.batchId = batchId;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const response = await axios.get(`${apiUrl}/api/admin/attendance-report`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      if (response.data.success) {
        setRecords(response.data.records || []);
      } else {
        setError(response.data.message || 'Failed to fetch attendance records.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching attendance records.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentSearch, batchId, dateFrom, dateTo]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    const timer = setTimeout(() => fetchRecords(), 300);
    return () => clearTimeout(timer);
  }, [fetchRecords]);

  const clearFilters = () => {
    setStudentSearch('');
    setBatchId('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Attendance Report</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}

          <div className="card mb-24">
            <div className="card-body">
              <div className="row g-16">
                <div className="col-md-3">
                  <label className="text-13 text-gray-500 mb-4 d-block">Student name / ID</label>
                  <input
                    type="text"
                    className="form-control py-9"
                    placeholder="Search student..."
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="text-13 text-gray-500 mb-4 d-block">Batch</label>
                  <select className="form-control py-9" value={batchId} onChange={(e) => setBatchId(e.target.value)}>
                    <option value="">All Batches</option>
                    {batches.map((b) => (
                      <option key={b.id} value={b.id}>{b.batchname} ({b.course_name})</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="text-13 text-gray-500 mb-4 d-block">From</label>
                  <input type="date" className="form-control py-9" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="text-13 text-gray-500 mb-4 d-block">To</label>
                  <input type="date" className="form-control py-9" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button type="button" className="btn btn-outline-secondary rounded-pill py-9 w-100" onClick={clearFilters}>
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Attendance Records</h5>
              <span className="text-13 text-gray-400">{records.length} record{records.length === 1 ? '' : 's'}</span>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : records.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No attendance records found for these filters.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Student ID</th>
                        <th>Batch</th>
                        <th>Course</th>
                        <th>Date</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r) => (
                        <tr key={r.id}>
                          <td>{r.firstName} {r.lastName}</td>
                          <td>{r.studentid}</td>
                          <td>{r.batchname || '—'}</td>
                          <td>{r.coursename || '—'}</td>
                          <td>{formatISTDate(r.attendance_date)}</td>
                          <td>{r.marked_at_display}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminAttendanceReport;
